import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "button", "banner", "status", "progress", "analyzed", "remaining", "summaryModal",
    "summaryAnalyzed", "summaryOutOfOffice", "summaryDeleted", "summaryNewEmail",
    "summaryLeftCompany", "summaryFailures", "summaryCleanupStatus", "cleanupButton"
  ]

  connect() {
    const message = window.sessionStorage.getItem("mailbox-local-cleanup-complete")
    if (!message) return

    window.sessionStorage.removeItem("mailbox-local-cleanup-complete")
    this.bannerTarget.hidden = false
    this.bannerTarget.classList.add("mailbox-organizer__analysis-banner--complete")
    this.statusTarget.textContent = message
    this.progressTarget.style.width = "100%"
  }

  async start() {
    if (this.running) return

    this.running = true
    this.buttonTarget.disabled = true
    this.bannerTarget.hidden = false
    this.bannerTarget.classList.remove("mailbox-organizer__analysis-banner--error", "mailbox-organizer__analysis-banner--complete")

    const total = Number.parseInt(this.buttonTarget.dataset.pending, 10) || 0
    let completed = 0
    let remaining = total
    let deleted = 0
    let deleteFailures = 0
    let outOfOffice = 0
    const tagged = { new_email_address: 0, left_company: 0 }
    let tagFailures = 0
    let succeeded = false
    try {
      while (remaining > 0) {
        const batchEnd = Math.min(completed + 10, total)
        this.statusTarget.textContent = `Analyzing emails ${completed + 1}–${batchEnd} of ${total}…`
        const response = await fetch(this.buttonTarget.dataset.url, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          },
          credentials: "same-origin",
          body: "{}"
        })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || "AI analysis failed")

        data.messages.forEach((message) => this.replaceMessage(message))
        completed += data.count
        outOfOffice += Number(data.out_of_office) || 0
        this.progressTarget.style.width = `${Math.min((completed / total) * 100, 100)}%`
        this.statusTarget.textContent = `Analyzed ${completed} of ${total} pending emails.`
        this.analyzedTarget.textContent = data.analyzed
        this.remainingTarget.textContent = `${data.remaining} waiting for analysis`
        remaining = data.remaining

        if (data.count === 0 && data.remaining > 0) throw new Error("Some emails still need analysis")
        if (data.remaining === 0) break
      }

      this.statusTarget.textContent = "Moving AI-classified out-of-office emails to Outlook Deleted Items…"
      let cleanupRemaining = 1
      while (cleanupRemaining > 0) {
        const response = await this.post(this.buttonTarget.dataset.deleteUrl)
        deleted += response.moved
        deleteFailures += response.failed
        cleanupRemaining = response.remaining
        this.statusTarget.textContent = `Moved ${deleted} out-of-office emails to Deleted Items…`
      }

      this.statusTarget.textContent = "Applying Outlook categories to company departures and new email addresses…"
      let categorizationRemaining = 1
      while (categorizationRemaining > 0) {
        const response = await this.post(this.buttonTarget.dataset.categorizeUrl)
        if (response.category && Object.prototype.hasOwnProperty.call(tagged, response.category)) {
          tagged[response.category] += response.tagged
        }
        tagFailures += response.failed
        categorizationRemaining = response.remaining
        const taggedTotal = tagged.new_email_address + tagged.left_company
        this.statusTarget.textContent = `Applied Outlook categories to ${taggedTotal} emails…`
      }

      this.bannerTarget.classList.add("mailbox-organizer__analysis-banner--complete")
      const failures = deleteFailures + tagFailures
      const failureMessage = failures > 0 ? ` ${failures} Outlook updates failed and remain available for review.` : ""
      const taggedTotal = tagged.new_email_address + tagged.left_company
      this.statusTarget.textContent = `Finished analyzing ${completed} emails, moved ${deleted} out-of-office emails, and tagged ${taggedTotal} emails in Outlook.${failureMessage}`
      this.showSummary(completed, outOfOffice, deleted, tagged, failures)
      succeeded = true
    } catch (error) {
      this.bannerTarget.classList.add("mailbox-organizer__analysis-banner--error")
      this.statusTarget.textContent = `${error.message}. ${completed} emails were completed; you can continue safely.`
    } finally {
      this.running = false
      this.buttonTarget.disabled = succeeded
    }
  }

  replaceMessage(message) {
    const current = this.element.querySelector(`[data-mailbox-message-id="${message.id}"]`)
    if (!current) return

    const template = document.createElement("template")
    template.innerHTML = message.html.trim()
    current.replaceWith(template.content.firstElementChild)
  }

  async post(url, method = "POST") {
    const response = await fetch(url, {
      method,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      credentials: "same-origin",
      body: "{}"
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || "Mailbox processing failed")
    return data
  }

  showSummary(analyzed, outOfOffice, deleted, tagged, failures) {
    this.summaryAnalyzedTarget.textContent = analyzed
    this.summaryOutOfOfficeTarget.textContent = outOfOffice
    this.summaryDeletedTarget.textContent = deleted
    this.summaryNewEmailTarget.textContent = tagged.new_email_address
    this.summaryLeftCompanyTarget.textContent = tagged.left_company
    this.summaryFailuresTarget.textContent = failures
    this.cleanupButtonTargets.forEach((button) => { button.hidden = false })
    this.summaryModalTarget.hidden = false
  }

  async clearProcessed() {
    if (this.clearing || this.running) return

    this.clearing = true
    this.cleanupButtonTargets.forEach((button) => { button.disabled = true })

    try {
      const result = await this.post(this.element.dataset.cleanupUrl, "DELETE")
      const retained = result.retained > 0 ? ` ${result.retained} unfinished or failed emails remain for review.` : ""
      window.sessionStorage.setItem(
        "mailbox-local-cleanup-complete",
        `Cleared ${result.deleted} processed local emails.${retained}`
      )
      window.location.reload()
    } catch (error) {
      this.summaryCleanupStatusTarget.hidden = false
      this.summaryCleanupStatusTarget.textContent = error.message
      this.bannerTarget.hidden = false
      this.bannerTarget.classList.add("mailbox-organizer__analysis-banner--error")
      this.statusTarget.textContent = error.message
      this.cleanupButtonTargets.forEach((button) => { button.disabled = false })
      this.clearing = false
    }
  }

  finish() {
    window.location.reload()
  }
}
